import React, { useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useMutation, useQuery } from "@tanstack/react-query";

import { deleteUserQualification, fetchUserQualificationsByType } from "../../../services/api/user";
import AddGeneralInsuranceModal from "../../CompleteYourProfile/components/AddGeneralInsuranceModal";
import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { UserType } from "../../../utils/enum";

const AddEditInsurance = ({ readOnly = false, userId, insuranceId, authRole }) => {
  const canManage = !readOnly && (authRole === UserType.FREELANCER || authRole === UserType.ADMIN);
  const [isAddEditInsuranceModalOpen, setIsAddEditInsuranceModalOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState();
  const [insuranceToDelete, setInsuranceToDelete] = useState(null);
  const {
    data: userQualificationsData,
    isPending: isUserQualificationsApiLoading,
    error: isUserQualificationsApiError,
    refetch: refetchUserQualificationsByType,
  } = useQuery({
    queryKey: ["equipment", userId, insuranceId],
    queryFn: () => fetchUserQualificationsByType(userId, insuranceId),
    enabled: !!userId && !!insuranceId,
  });
  const {
    isPending: isDeleteUserQualificationApiLoading,
    error: isDeleteUserQualificationApiError,
    data: deletedUserQualificationsData,
    mutateAsync: deleteUserQualificationMutation,
  } = useMutation({
    mutationFn: ({ id, targetUserId }) => deleteUserQualification(id, targetUserId),
    enabled: false,
  });

  const handleEditInsuranceButtonClicked = (insurance) => {
    setSelectedInsurance(insurance);
    setIsAddEditInsuranceModalOpen(true);
  };

  const handleDeleteInsurance = async () => {
    if (!insuranceToDelete) return;
    try {
      await deleteUserQualificationMutation({ id: insuranceToDelete.id, targetUserId: userId });
      await refetchUserQualificationsByType();
      setInsuranceToDelete(null);
    } catch (error) {}
  };

  return (
    <BaseCard className="py-2 px-4 sm:px-6 rounded-lg" elevation={1}>
      <div className="bg-light px-4 py-2 mt-2 mb-2 rounded-md flex justify-between items-center">
        <h6 className="text-dark font-normal">Insurance</h6>

        {canManage && (
          <i
            className="fa-solid fa-circle-plus cursor-pointer text-xl text-secondary"
            onClick={() => setIsAddEditInsuranceModalOpen(true)}
          ></i>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 my-4 px-4">
        {userQualificationsData?.data?.map((insurance) => (
          <div className="bg-light rounded-md py-3 px-4 flex justify-between border">
            <div>
              <div className="flex mb-1.5">
                <p className="font-medium w-32">Insurance Type: </p>
                <p>{insurance?.qualificationSubType}</p>
              </div>
              {(insurance?.url || insurance?.digitalAsset?.url) && (
                <div className="flex items-center">
                  <p className="font-medium w-32">Attachment: </p>
                  <a
                    href={insurance?.url || insurance?.digitalAsset?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-[4px] py-1 pl-2 pr-3 cursor-pointer text-blue-500 hover:underline font-300"
                  >
                    <i className="fa-solid fa-paperclip"></i>
                    {(
                      insurance?.fileName ||
                      insurance?.digitalAsset?.fileName ||
                      "Attachment"
                    ).slice(0, 20)}
                  </a>
                </div>
              )}
            </div>
            <div className="flex items-start shrink-0">
              {canManage && (
                <i
                  className="fa-regular fa-trash-can text-red-600 cursor-pointer mr-3"
                  onClick={() => setInsuranceToDelete(insurance)}
                ></i>
              )}
              {canManage && (
                <i
                  className="fa-light fa-pencil text-gray-900 cursor-pointer"
                  onClick={() => handleEditInsuranceButtonClicked(insurance)}
                ></i>
              )}
            </div>
          </div>
        ))}
      </div>

      {insuranceToDelete && (
        <BaseConfirmModal
          open={!!insuranceToDelete}
          onClose={() => setInsuranceToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Insurance"
          description={`Are you sure you want to delete "${insuranceToDelete?.qualificationSubType}"? This action cannot be undone.`}
          isLoading={isDeleteUserQualificationApiLoading}
          onConfirm={handleDeleteInsurance}
        />
      )}

      {isAddEditInsuranceModalOpen && canManage && (
        <AddGeneralInsuranceModal
          refetchUserQualificationsByType={refetchUserQualificationsByType}
          open={isAddEditInsuranceModalOpen}
          close={setIsAddEditInsuranceModalOpen}
          insuranceId={insuranceId}
          selectedInsurance={selectedInsurance}
          setSelectedInsurance={setSelectedInsurance}
          targetUserId={userId}
        />
      )}
    </BaseCard>
  );
};

export default AddEditInsurance;
